import React, { Suspense, lazy } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import './index.css';
import detectStudyLanguage from './studyDetect';

const rootElement = document.getElementById('root');

// Language-study sites (spanish./german.secondlifesoftware.com) share this build.
// Branch BEFORE the marketing app mounts: the study app has its own theme system
// (three-state light/dark/system), so the global ThemeProvider — which force-sets
// data-theme="light" on <html> — must not mount in study mode. Lazy import keeps
// the study chunk (and its :root design tokens) out of every marketing visitor's
// bundle.
const studyLanguage = detectStudyLanguage();

if (studyLanguage) {
  const StudyApp = lazy(() => import('./study/StudyApp'));
  // The static build serves the react-snap snapshot of the marketing homepage on
  // every path — subdomains included. That markup is a different app entirely, so
  // hydrating it would mismatch; drop it and mount fresh.
  rootElement.innerHTML = '';
  createRoot(rootElement).render(
    <React.StrictMode>
      <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
        <StudyApp language={studyLanguage} />
      </Suspense>
    </React.StrictMode>
  );
} else {
  // Marketing / admin app — unchanged path, including react-snap hydration.
  const App = require('./App').default;
  const { ThemeProvider } = require('./contexts/ThemeContext');
  const { AuthProvider } = require('./contexts/AuthContext');

  const app = (
    <React.StrictMode>
      <AuthProvider>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </AuthProvider>
    </React.StrictMode>
  );

  // react-snap prerenders static HTML into #root at build time. When that markup
  // is present, hydrate it; otherwise (normal dev / non-prerendered) mount fresh.
  if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, app);
  } else {
    createRoot(rootElement).render(app);
  }
}
